/**
 * Root entry. Mirrors the web app's overall structure:
 *   garageiq-web/app/(main)/layout.js  → MainTabs (consumer)
 *   garageiq-web/app/shop/layout.js    → ShopStack (shop owner portal)
 *
 * StatusBar + SafeAreaProvider live here so every screen can rely on insets.
 * The global stylesheet must be imported once at the root for NativeWind.
 *
 * The whole app is gated behind a Cognito login (early access). AuthGate
 * switches between a loading splash, the LoginScreen, and the authenticated
 * RootNavigator based on the AuthProvider's status.
 */
import './global.css';

import React from 'react';
import { ActivityIndicator, StatusBar, View } from 'react-native';
import { SafeAreaProvider } from 'react-native-safe-area-context';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import { NavigationContainer } from '@react-navigation/native';

import RootNavigator from './src/navigation/RootNavigator';
import LoginScreen from './src/screens/LoginScreen';
import { AuthProvider, useAuth } from './src/lib/auth';
import { navigationTheme, colors } from './src/lib/theme';

function AuthGate() {
  const { status } = useAuth();

  if (status === 'loading') {
    return (
      <View style={{ flex: 1, backgroundColor: colors.appBackground, alignItems: 'center', justifyContent: 'center' }}>
        <ActivityIndicator color="#fff" />
      </View>
    );
  }

  if (status === 'signedOut') {
    return <LoginScreen />;
  }

  return (
    <NavigationContainer theme={navigationTheme}>
      <RootNavigator />
    </NavigationContainer>
  );
}

export default function App() {
  return (
    <GestureHandlerRootView style={{ flex: 1, backgroundColor: colors.appBackground }}>
      <SafeAreaProvider>
        <StatusBar barStyle="light-content" backgroundColor={colors.appBackground} />
        <AuthProvider>
          <AuthGate />
        </AuthProvider>
      </SafeAreaProvider>
    </GestureHandlerRootView>
  );
}
